import { useEffect, useState } from "react";
import { ObserveRefreshData, Vehicle } from "../../../../types/observe";
import {
  audioMix,
  vehicleChangeSelectCar,
  sendLogSave,
  sendOperationCommand,
  sendUpdateScreen,
} from "../../../../utils/sendData";
import {
  sendYmcEmergencyStopAll,
  sendYmcOutOfServiceAll,
  sendYmcCommand,
} from "../../../../utils/ymc-client";
import { isEiheijiEnvironment } from "../../../../utils/environment";
import ConfirmModal from "../../../../components/confirmModal/ConfirmModal";

import "./ObserveFooter.scss";

interface ObserveFooterProps {
  refreshDataMap: Record<string, ObserveRefreshData | null>;
  monitorConsoleId: number | null;
  layoutVehicles: (Vehicle | null)[];
  observeCC: string | null;
  onevehicleCC: string | null;
  sendData: (msg: object) => void;
  isAnyRemoteMode?: boolean;
}

const ObserveFooter = ({
  refreshDataMap,
  monitorConsoleId,
  layoutVehicles,
  observeCC,
  onevehicleCC,
  sendData,
  isAnyRemoteMode = false,
}: ObserveFooterProps) => {
  const vehicles = layoutVehicles.filter((v): v is Vehicle => v !== null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedVehicleCode, setSelectedVehicleCode] = useState("");
  const [isInternalAudioOn, setIsInternalAudioOn] = useState(false);
  const [broadcastState, setBroadcastState] = useState<
    "inactive" | "off" | "on"
  >("inactive");
  const [guideLabel, setGuideLabel] = useState("ガイド");

  const sr = observeCC;

  const isMyVehicleSelected = Object.values(refreshDataMap).some(
    (data) => data?.info?.is_selecting === "1"
  );

  useEffect(() => {
    for (const key in refreshDataMap) {
      const data = refreshDataMap[key];
      if (data?.info?.is_selecting === "1") {
        const guide = data.info.guide_switching;
        if (guide === "0") {
          setGuideLabel("ガイド0");
        } else if (guide === "1") {
          setGuideLabel("ガイド1");
        } else {
          setGuideLabel("ガイド2");
        }
        break;
      }
    }

    const firstMonitor = Object.values(refreshDataMap).find(
      (data) => data?.monitor != null
    )?.monitor;

    if (firstMonitor) {
      const isAllAudio = firstMonitor.all_audio_switching === "1";
      const isAllVoice = firstMonitor.all_voice_switching === "1";
      setIsInternalAudioOn(isAllAudio);

      if (isAllAudio && isAllVoice) {
        setBroadcastState("on");
      } else if (isAllAudio && !isAllVoice) {
        setBroadcastState("off");
      } else {
        setBroadcastState("inactive");
      }
    }
  }, [refreshDataMap]);

  const execDeselect = (vcc: string) => {
    if (!sr || !onevehicleCC || !monitorConsoleId || !refreshDataMap[vcc]) return;

    if (refreshDataMap[vcc]?.info?.is_selecting === "1") {
      sendData(vehicleChangeSelectCar(sr, vcc, 0));
      // ガイド線指示は画面更新指示に統合
      sendData(sendUpdateScreen(sr, onevehicleCC, 2, vcc, 0, 0));
    }
  };

  const execDeselectAll = () => {
    if (!sr || !onevehicleCC || !monitorConsoleId) return;

    for (const [key, value] of Object.entries(refreshDataMap)) {
      if (!value) continue;
      // 車両選択解除
      execDeselect(key);
    }
  }

  const handleInternalAudioAll = () => {
    if (!sr) return;

    const mic = isInternalAudioOn ? 0 : 1;
    const spk = 0;
    sendData(audioMix(sr, "00000000", mic, spk));
  };

  const handleBroadcastAll = () => {
    if (!sr || broadcastState === "inactive") return;

    let mic = 1;
    let spk = 0;
    if (broadcastState === "on") {
      mic = 1;
      spk = 0;
    } else if (broadcastState === "off") {
      mic = 1;
      spk = 1;
    }
    sendData(audioMix(sr, "00000000", mic, spk));
  };

  const handleGuide = () => {
    if (!sr || !onevehicleCC) return;

    for (const key in refreshDataMap) {
      const data = refreshDataMap[key];
      if (data?.info?.is_selecting === "1") {
        const guide = data.info.guide_switching;

        // ガイド線指示は画面更新指示に統合
        if (guide === "0") {
          sendData(sendUpdateScreen(sr, onevehicleCC, 2, key, 0, 1));
        } else if (guide === "1") {
          sendData(sendUpdateScreen(sr, onevehicleCC, 2, key, 0, 2));
        } else {
          sendData(sendUpdateScreen(sr, onevehicleCC, 2, key, 0, 0));
        }
        break;
      }
    }
  };

  const handleEndMonitoring = () => {
    setIsModalOpen(true);
  };

  const handleConfirm = () => {
    setIsModalOpen(false);
    if (!sr) return;

    if (selectedVehicleCode !== "") {
      if (!refreshDataMap[selectedVehicleCode]) return;
      // 車両選択解除
      execDeselect(selectedVehicleCode);
    } else {
      execDeselectAll();
    }

    vehicles.forEach((v) => {
      const vcc = v.vehicle_communication_code;
      if (selectedVehicleCode === "" || selectedVehicleCode === vcc) {
        for (let dev = 0; dev < 4; dev++) {
          sendData(sendLogSave(sr, vcc, dev));
        }

        // 永平寺環境のみ
        // YMC個車運用外処理を実行する
        if (isEiheijiEnvironment() && selectedVehicleCode !== "") {
          sendYmcCommand("out-of-service", v.vehicles_vehicle_id);
        }
      }
    });

    // 永平寺環境のみ
    if (isEiheijiEnvironment() && selectedVehicleCode === "") {
      // YMC全車運用外処理を実行する
      sendYmcOutOfServiceAll();
    }

    // TextRoomに操作ログ通知（全車のみ）
    if (selectedVehicleCode === "") {
      // ログ代わりに、操作をTextroomに送信
      sendData(sendOperationCommand(sr, sr, "out-of-service-all"));
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  //[全車緊急停止]ボタンを押下
  // 自動運転の全車両に準緊急停止を指示する
  const handleEmergencyStopAll = () => {
    console.log("*****DEBUG:stopAllCar.click");

    sendYmcEmergencyStopAll();

    // ログ代わりに、操作をTextroomに送信
    if (sr) {
      sendData(sendOperationCommand(sr, sr, "emergency-stop-all"));
    }
  };

  return (
    <>
      <div
        className={`observeFooterArea ${
          isEiheijiEnvironment() ? "eiheiji-layout" : ""
        }`}
      >
        <div className="buttonWrapper">
          <button onClick={execDeselectAll} disabled={isAnyRemoteMode}>
            車両選択解除
          </button>
          <button
            className={`internalAudioBtn ${isInternalAudioOn ? "active" : ""}`}
            onClick={handleInternalAudioAll}
          >
            全車車内音声
          </button>
          <button
            className={`broadcastBtn ${
              broadcastState === "on"
                ? "active"
                : broadcastState === "off"
                ? "inactive"
                : ""
            }`}
            disabled={broadcastState === "inactive"}
            onClick={handleBroadcastAll}
          >
            全車一斉通知
          </button>
          <button
            className={`guideBtn ${guideLabel !== "ガイド0" ? "active" : ""}`}
            disabled={!isMyVehicleSelected}
            onClick={handleGuide}
          >
            {guideLabel}
          </button>
          <select
            value={selectedVehicleCode}
            onChange={(e) => setSelectedVehicleCode(e.target.value)}
          >
            <option value="">全車両</option>
            {vehicles.map((v) => (
              <option
                key={v.vehicles_vehicle_id}
                value={v.vehicle_communication_code}
              >
                {v.vehicle_label}
              </option>
            ))}
          </select>
          <button className="monitorEndBtn" onClick={handleEndMonitoring}>
            監視終了
          </button>
          {isEiheijiEnvironment() && (
            <button
              className="emergencyStopAllBtn"
              onClick={handleEmergencyStopAll}
            >
              全車緊急停止
            </button>
          )}
        </div>
      </div>
      <ConfirmModal
        isOpen={isModalOpen}
        icon="question"
        message="監視業務を終了しますか？"
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </>
  );
};

export default ObserveFooter;
